(() => {
  if (window.MBPriceRouter) return;

  const node = document.getElementById('MBPriceRouterConfig');
  if (!node) return;

  let config;
  try {
    config = JSON.parse(node.textContent);
  } catch (error) {
    console.error('MB Price Router: invalid config', error);
    return;
  }

  const targets = [...config.targets].sort((a, b) => a.cents - b.cents);
  const byCents = new Map(targets.map((target) => [Number(target.cents), target]));
  const blockedExtraParams = new Set((config.blockedExtraParams || []).map(String));
  const euro = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' });
  let currentRoute = null;

  const money = (cents) => euro.format(Number(cents || 0) / 100);

  const buildCheckoutUrl = (cents, extraParams = {}) => {
    const target = byCents.get(Number(cents));
    if (!target?.url) return null;

    const url = new URL(target.url, window.location.origin);
    blockedExtraParams.forEach((key) => url.searchParams.delete(key));
    if (config.tracking?.sub1) url.searchParams.set('sub1', config.tracking.sub1);
    if (config.tracking?.sub5) url.searchParams.set('sub5', config.tracking.sub5);

    Object.entries(extraParams).forEach(([key, value]) => {
      if (!blockedExtraParams.has(String(key)) && value !== undefined && value !== null && String(value) !== '') {
        url.searchParams.set(key, String(value));
      }
    });

    return url.toString();
  };

  const route = (rawTotalCents) => {
    const raw = Number(rawTotalCents || 0);
    if (raw <= 0) return { state: 'EMPTY', routable: false, raw, discount: 0 };
    if (raw > Number(config.maxOrderCents)) return { state: 'MAX_ORDER', routable: false, raw, discount: 0 };

    if (byCents.has(raw)) return { state: 'EXACT', routable: true, raw, target: raw, discount: 0 };

    let lower = null;
    let upper = null;

    for (const target of targets) {
      if (target.cents < raw) lower = target;
      if (target.cents > raw) {
        upper = target;
        break;
      }
    }

    if (lower) {
      const discount = raw - lower.cents;
      if (discount <= Number(config.maxDiscountCents)) {
        return {
          state: 'SMART_DISCOUNT',
          routable: true,
          raw,
          target: lower.cents,
          discount
        };
      }
    }

    if (upper && upper.cents <= Number(config.maxOrderCents)) {
      return {
        state: 'NEXT_TIER',
        routable: false,
        raw,
        nextTarget: upper.cents,
        amountToNext: upper.cents - raw,
        discount: 0
      };
    }

    return { state: 'BLOCKED', routable: false, raw, discount: 0 };
  };

  const quantityViolation = (cart) => {
    if (!Array.isArray(cart?.items)) return null;

    for (const item of cart.items) {
      const price = String(Number(item.price || 0));
      const max = Number(config.quantityRules?.[price] || 1);
      const quantity = Number(item.quantity || 0);

      if (quantity > max) {
        return {
          productTitle: item.product_title || item.title || 'cet article',
          maxQuantity: max,
          quantity
        };
      }
    }

    return null;
  };

  const compactComposition = (cart) => {
    if (!Array.isArray(cart?.items)) return '';
    return cart.items
      .map((item) => String(item.variant_id || item.id) + 'x' + String(item.quantity || 1))
      .join('|')
      .slice(0, 800);
  };

  const extraParamsForCart = (cart, result) => ({
    sub2: String(config.shopCode || 'MB') + '-CART',
    sub3: compactComposition(cart),
    sub4: 'Commande My Bradery'
  });

  const updateSummary = (root, result) => {
    const rawNode = root.querySelector('[data-mb-router-raw]');
    const couponRow = root.querySelector('[data-mb-router-coupon-row]');
    const couponNode = root.querySelector('[data-mb-router-coupon]');
    const totalNode = root.parentElement?.querySelector('[data-mb-router-total]') || document.querySelector('[data-mb-router-total]');
    const statusNode = root.querySelector('[data-mb-router-status]');

    if (rawNode) rawNode.textContent = money(result.raw);

    if (couponRow && couponNode) {
      const showCoupon = result.routable && result.discount > 0;
      couponRow.hidden = !showCoupon;
      couponNode.textContent = showCoupon ? '-' + money(result.discount) : '';
    }

    if (totalNode) totalNode.textContent = money(result.routable ? result.target : result.raw);

    if (statusNode) {
      let text = '';
      let state = '';

      if (result.state === 'SMART_DISCOUNT') {
        text = '✓ ' + config.labels.bestDiscount;
        state = 'success';
      } else if (result.state === 'NEXT_TIER') {
        text = config.labels.nextTier.replace('[amount]', money(result.amountToNext));
        state = 'next';
      } else if (result.state === 'MAX_ORDER') {
        text = config.labels.maxOrder.replace('[amount]', money(config.maxOrderCents));
        state = 'blocked';
      } else if (result.state === 'QTY_LIMIT') {
        text = config.labels.quantityLimit
          .replace('[max]', String(result.maxQuantity))
          .replace('[product]', result.productTitle);
        state = 'blocked';
      } else if (result.state === 'BLOCKED') {
        text = config.labels.unavailable;
        state = 'blocked';
      }

      statusNode.hidden = !text;
      statusNode.textContent = text;
      statusNode.dataset.state = state;
    }
  };

  const applyUI = (result) => {
    currentRoute = result;
    document.querySelectorAll('[data-mb-router-summary]').forEach((root) => updateSummary(root, result));

    document.querySelectorAll('[data-mb-router-checkout]').forEach((button) => {
      const disabled = !result.routable || result.state === 'EMPTY';
      button.disabled = disabled;
      button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
      button.textContent = result.routable
        ? config.labels.checkout + ' · ' + money(result.target)
        : config.labels.checkout;
    });
  };

  const syncFromDom = () => {
    const summary = document.querySelector('[data-mb-router-summary]');
    if (!summary) return null;
    const raw = Number(summary.dataset.rawTotal || 0);
    const result = route(raw);
    applyUI(result);
    return result;
  };

  const renderCartData = (cart) => {
    if (!cart || cart.original_total_price === undefined) return syncFromDom();

    const violation = quantityViolation(cart);
    const result = violation
      ? {
          state: 'QTY_LIMIT',
          routable: false,
          raw: Number(cart.original_total_price || 0),
          discount: 0,
          ...violation
        }
      : route(cart.original_total_price);

    applyUI(result);
    return result;
  };

  const fetchCart = async () => {
    const response = await fetch((window.Shopify?.routes?.root || '/') + 'cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('Cart fetch failed');
    return response.json();
  };

  const goToTarget = (cents, extraParams = {}) => {
    const url = buildCheckoutUrl(Number(cents), extraParams);
    if (!url) return false;
    window.location.assign(url);
    return true;
  };

  window.MBPriceRouter = {
    config,
    route,
    refresh: renderCartData,
    syncFromDom,
    buildCheckoutUrl,
    goToTarget,
    money
  };

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-mb-router-checkout]');
    if (!button) return;

    event.preventDefault();
    button.disabled = true;

    try {
      const cart = await fetchCart();
      const result = renderCartData(cart);
      if (!result?.routable) return;

      const url = buildCheckoutUrl(result.target, extraParamsForCart(cart, result));
      if (url) window.location.assign(url);
    } catch (error) {
      console.error('MB Price Router:', error);
      button.disabled = false;
    }
  });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS?.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event?.cartData?.original_total_price !== undefined) renderCartData(event.cartData);
      window.setTimeout(syncFromDom, 180);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncFromDom, { once: true });
  } else {
    syncFromDom();
  }
})();